import type { AuditStatus, Id } from "@/packages/types";
import { api } from "../../../convex/_generated/api";
import { useApiMutation } from "@/packages/features-shared";
import { useTranslation } from "@/packages/utils";

export type AuditAction =
  | "submit"
  | "approve"
  | "reject"
  | "archive"
  | "unarchive"
  | "resubmit"
  | "move_back";

export function useAuditStatusChange() {
  const { t } = useTranslation("audits");

  const validateReadinessMutation = useApiMutation(
    api.audit_validate_readiness.validateReadiness,
    {
      name: "validate-readiness",
      silentSuccess: true,
      silentErrors: true,
    },
  );
  const submitMutation = useApiMutation(api.audit_submit.submitForReview, {
    name: "submit-for-review",
    successMessage: t("messages.submittedForReview"),
  });
  const approveMutation = useApiMutation(api.audit_approve.approveAudit, {
    name: "approve-audit",
    successMessage: t("messages.approved"),
  });
  const rejectMutation = useApiMutation(api.audit_reject.rejectAudit, {
    name: "reject-audit",
    successMessage: t("messages.rejected"),
  });
  const archiveMutation = useApiMutation(api.audit_archive.archiveAudit, {
    name: "archive-audit",
    successMessage: t("messages.archived"),
  });
  const unarchiveMutation = useApiMutation(api.audit_unarchive.unarchiveAudit, {
    name: "unarchive-audit",
    successMessage: t("messages.restoredToInProgress"),
  });
  const resubmitMutation = useApiMutation(api.audit_resubmit.resubmitAudit, {
    name: "resubmit-audit",
    successMessage: t("messages.resubmitted"),
  });
  const moveBackMutation = useApiMutation(
    api.audit_mutations.updateAuditStatus,
    {
      name: "move-back-audit",
      successMessage: t("messages.statusUpdated"),
    },
  );

  async function validateReadiness(auditId: Id<"audits">) {
    return validateReadinessMutation.mutateAsync({ auditId });
  }

  async function execute(
    action: AuditAction,
    auditId: Id<"audits">,
    options?: { reason?: string; targetStatus?: AuditStatus },
  ) {
    switch (action) {
      case "submit":
        return submitMutation.mutateAsync({ auditId });
      case "approve":
        return approveMutation.mutateAsync({ auditId });
      case "reject":
        return rejectMutation.mutateAsync({
          auditId,
          reason: options?.reason ?? "",
        });
      case "archive":
        return archiveMutation.mutateAsync({ auditId });
      case "unarchive":
        return unarchiveMutation.mutateAsync({ auditId });
      case "resubmit":
        return resubmitMutation.mutateAsync({ auditId });
      case "move_back":
        if (!options?.targetStatus) {
          throw new Error("targetStatus is required for move_back action");
        }
        return moveBackMutation.mutateAsync({
          auditId,
          status: options.targetStatus,
        });
    }
  }

  const isPending =
    validateReadinessMutation.isPending ||
    submitMutation.isPending ||
    approveMutation.isPending ||
    rejectMutation.isPending ||
    archiveMutation.isPending ||
    unarchiveMutation.isPending ||
    resubmitMutation.isPending ||
    moveBackMutation.isPending;

  return { execute, validateReadiness, isPending };
}
